import { ArrowLeft, User, MapPin, Receipt, CreditCard } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CpnBeneficiaryUser } from '@/types/cpn'
import {
  CPN_PAYMENT_REASONS,
  PAYMENT_REASON_LABELS,
  type CpnPaymentReason,
} from '@/utils/cpn'
import { METADATA_FIELD_CONFIG } from '@/utils/validation'

interface BeneficiaryViewScreenProps {
  beneficiary: CpnBeneficiaryUser
  onBack: () => void
}

// Helper function to safely extract metadata values
const getMetadataValue = (
  metadata: Record<string, unknown> | undefined,
  key: string
): string => {
  if (!metadata || typeof metadata[key] !== 'string') {
    return ''
  }
  return metadata[key] as string
}

const BeneficiaryViewScreen = ({
  beneficiary,
  onBack,
}: BeneficiaryViewScreenProps) => {
  // Only include metadata fields that have actual values
  const getExistingMetadata = (): Record<string, string> => {
    const existingFields: Record<string, string> = {}
    const allPossibleFields = {
      ACCOUNT_NUMBER: getMetadataValue(beneficiary.metadata, 'ACCOUNT_NUMBER'),
      BANK_NAME: getMetadataValue(beneficiary.metadata, 'BANK_NAME'),
      BANK_CODE: getMetadataValue(beneficiary.metadata, 'BANK_CODE'),
      BANK_COUNTRY: getMetadataValue(beneficiary.metadata, 'BANK_COUNTRY'),
      CLABE: getMetadataValue(beneficiary.metadata, 'CLABE'),
      RECIPIENT_ID_NUMBER: getMetadataValue(
        beneficiary.metadata,
        'RECIPIENT_ID_NUMBER'
      ),
      SWIFT_CODE: getMetadataValue(beneficiary.metadata, 'SWIFT_CODE'),
    }

    // Only include fields that have values
    Object.entries(allPossibleFields).forEach(([key, value]) => {
      if (value && value.trim() !== '') {
        existingFields[key] = value
      }
    })

    return existingFields
  }

  const metadataData = getExistingMetadata()
  const paymentReason =
    getMetadataValue(beneficiary.metadata, 'paymentReason') || 'PMT022'

  // Function to render dynamic metadata fields (read-only)
  const renderMetadataFields = () => {
    const metadataKeys = Object.keys(metadataData)

    if (metadataKeys.length === 0) {
      return null // Don't show the section if no metadata exists
    }

    return (
      <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
        <div className="flex items-center space-x-2 mb-3">
          <CreditCard className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <h3 className="font-medium text-gray-900 dark:text-white">
            Bank Information
          </h3>
        </div>

        <div className="space-y-3">
          {metadataKeys.map((fieldKey) => {
            const config = METADATA_FIELD_CONFIG[fieldKey]
            if (!config) return null

            return (
              <div key={fieldKey}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {config.label}
                </label>
                <Input
                  value={metadataData[fieldKey] || ''}
                  readOnly
                  className="dark:bg-gray-900 dark:border-gray-600 dark:text-white bg-gray-50 cursor-default"
                />
              </div>
            )
          })}
        </div>
      </Card>
    )
  }

  return (
    <div className="flex flex-col h-[600px] dark:bg-gray-900">
      <div className="p-4 space-y-4 flex-1 overflow-y-auto dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center space-x-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="p-1">
            <ArrowLeft className="w-5 h-5 dark:text-gray-300" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              View Recipient
            </h1>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Contact Support at Support@paytrie.com to modify details
            </p>
          </div>
        </div>

        {/* Personal Information */}
        <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-3">
            <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <h3 className="font-medium text-gray-900 dark:text-white">
              Personal Information
            </h3>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name
                </label>
                <Input
                  value={beneficiary.firstName || ''}
                  readOnly
                  className="dark:bg-gray-900 dark:border-gray-600 dark:text-white bg-gray-50 cursor-default"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name
                </label>
                <Input
                  value={beneficiary.lastName || ''}
                  readOnly
                  className="dark:bg-gray-900 dark:border-gray-600 dark:text-white bg-gray-50 cursor-default"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Payment Reason */}
        <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-3">
            <Receipt className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <h3 className="font-medium text-gray-900 dark:text-white">
              Payment Reason
            </h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reason for Payment
            </label>
            <Select value={paymentReason} disabled>
              <SelectTrigger className="w-full dark:bg-gray-900 dark:border-gray-600 dark:text-white bg-gray-50 cursor-default">
                <SelectValue placeholder="Select payment reason" />
              </SelectTrigger>
              <SelectContent className="dark:bg-gray-900 dark:border-gray-600 max-h-[300px] max-w-[365px]">
                {CPN_PAYMENT_REASONS.map((code) => (
                  <SelectItem
                    key={code}
                    value={code}
                    className="dark:text-white dark:focus:bg-gray-700 dark:focus:text-white text-sm"
                  >
                    {PAYMENT_REASON_LABELS[code as CpnPaymentReason]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Address Information */}
        <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-3">
            <MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <h3 className="font-medium text-gray-900 dark:text-white">
              Address Information
            </h3>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Street Address
              </label>
              <Input
                value={beneficiary.street || ''}
                readOnly
                className="dark:bg-gray-900 dark:border-gray-600 dark:text-white bg-gray-50 cursor-default"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  City
                </label>
                <Input
                  value={beneficiary.city || ''}
                  readOnly
                  className="dark:bg-gray-900 dark:border-gray-600 dark:text-white bg-gray-50 cursor-default"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  State/Province
                </label>
                <Input
                  value={beneficiary.stateProvince || ''}
                  readOnly
                  className="dark:bg-gray-900 dark:border-gray-600 dark:text-white bg-gray-50 cursor-default"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Postal Code
                </label>
                <Input
                  value={beneficiary.postalCode || ''}
                  readOnly
                  className="dark:bg-gray-900 dark:border-gray-600 dark:text-white bg-gray-50 cursor-default"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Country Code
                </label>
                <Input
                  value={beneficiary.country || ''}
                  readOnly
                  className="dark:bg-gray-900 dark:border-gray-600 dark:text-white bg-gray-50 cursor-default"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Dynamic Bank Information - only shows fields that have values (read-only) */}
        {renderMetadataFields()}
      </div>
    </div>
  )
}

export default BeneficiaryViewScreen
